import axios from 'axios';
import moment from 'moment';

// Used for to populate door state on initial load and reset door state on logout
const getDoorDefaultState = () => {
  return {
		doorToOpenReaderId: null,
		doorAndLocationData: null,
		doorDataSelectedLocationId: null,
		userAccessRights: null,
		lastSuccessfulLocationConfirmation: { timestamp: null, position: null, locationId: null },
		// Proactive location watching - for faster door opening
		currentWatchedPosition: null,
		locationWatchId: null,
  }
}

export default {
	namespaced: true,
	state: getDoorDefaultState(),
	getters: {
		/**
		 * Get recent valid location confirmation if it exists and is still valid
		 * @param {Object} state
		 * @param {Object} rootState
		 * @returns {Object|null} Confirmation object or null if not valid
		 */
		recentValidLocationConfirmation: (state, getters, rootState) => {
			const confirmation = state.lastSuccessfulLocationConfirmation;
			if (!confirmation || !confirmation.timestamp || !confirmation.position) {
				return null;
			}

			const settings = rootState.common.settings;
			const reuseMinutes = settings.reuseLocationConfirmationLastMinutes || 0;
			if (reuseMinutes <= 0) {
				return null; // Feature disabled
			}

			const now = Date.now();
			const confirmationAge = now - confirmation.timestamp;
			const maxAge = reuseMinutes * 60 * 1000; // Convert minutes to milliseconds

			if (confirmationAge > maxAge) {
				return null; // Confirmation expired
			}

			return confirmation;
		},
		/**
		 * Get recent watched position if it exists and is fresh enough (less than 30 seconds old)
		 * @param {Object} state
		 * @returns {Object|null} Position object or null if not valid
		 */
		recentWatchedPosition: (state) => {
			const position = state.currentWatchedPosition;
			if (!position || !position.timestamp) {
				return null;
			}

			const now = Date.now();
			const positionAge = now - position.timestamp;
			const maxAge = 30 * 1000; // 30 seconds max age for watched position

			if (positionAge > maxAge) {
				return null; // Position too old
			}

			return position;
		}
	},
	mutations: {
		mutateDoorStateToDefault(state) {
			Object.assign(state, getDoorDefaultState())
		},
		mutateDoorToOpenReaderId(state, doorToOpenReaderId) {
			state.doorToOpenReaderId = doorToOpenReaderId;
		},
		mutateDoorAndLocationData(state, doorAndLocationData) {
			state.doorAndLocationData = doorAndLocationData;
		},
		mutateDoorDataSelectedLocationId(state, doorDataSelectedLocationId) {
			state.doorDataSelectedLocationId = doorDataSelectedLocationId;
		},
		mutateUserAccessRights(state, userAccessRights) {
			state.userAccessRights = userAccessRights;
		},
		mutateLastSuccessfulLocationConfirmation(state, confirmation) {
			state.lastSuccessfulLocationConfirmation = confirmation;
		},
		clearLastSuccessfulLocationConfirmation(state) {
			state.lastSuccessfulLocationConfirmation = null;
		},
		mutateCurrentWatchedPosition(state, position) {
			state.currentWatchedPosition = position;
		},
		mutateLocationWatchId(state, watchId) {
			state.locationWatchId = watchId;
		},
		clearWatchedPosition(state) {
			state.currentWatchedPosition = null;
			state.locationWatchId = null;
		}
	},
	actions: {
		async getDoorAndLocationDataFromServer({ rootGetters, commit }) {
			try {
				const args = {
					includePaused: true
				}
				const { data } = await axios.post(`${rootGetters["common/activeHost"].ajaxUrl}?controller=ajax&getaccesscontrolappdata=1&appauth=${rootGetters["common/activeHost"].appauth}`, args)
				commit('mutateDoorAndLocationData', data)

				// If door data has only one location set locationId
				if (data.locations.length === 1) {
					commit('mutateDoorDataSelectedLocationId', data.locations[0].locationId)
				}
				return data
			} catch (error) {
				return Promise.reject(error)
			}
		},
		async getUserAccessRights({ rootGetters, rootState, commit }) {
			const personId = rootState.user.user.personId
			try {
				let data

				if (rootState.user.subProfileActive) {
					data = await axios.get(`${rootGetters["common/activeHost"].restUrl}accessrights/`,
						{
							params:{ personId, personLinkReq: 1 }
						}
					)
				} else {
					data = await axios.get(`${rootGetters["common/activeHost"].restUrl}accessrights/`)
				}


				if (typeof data === 'object' && data?.data.rows?.length) {

					const accessRights = [];

					data.data.rows.forEach(item => 
						accessRights.push({
							productName: item.productName || null,
							usableQuantity: item.usableQuantity || null,
							validFrom: item.validFrom ? moment(item.validFrom).startOf('day') : null,
							validTo: item.validTo ? moment(item.validTo).endOf('day') : null,
							orderId: item.orderId || null,
							futureValidFound: (item.validFrom && moment(item.validFrom).isAfter(moment(), 'day') &&
								(!item.validTo || moment(item.validTo).isAfter(moment(), 'day'))),
							continuousFound: ((!item.validFrom || moment(item.validFrom).isSameOrBefore(moment(), 'day')) && !item.validTo),
							currentlyValidFound: ((!item.validFrom || moment(item.validFrom).isSameOrBefore(moment(), 'day')) &&
								(!item.validTo || moment(item.validTo).isSameOrAfter(moment(), 'day'))),
							errorStatus: item.errorStatus,
							name: item.name || null
						})
					)

					commit('mutateUserAccessRights', accessRights)
				}
			} catch (error) {
				return Promise.reject(error)
			}
		}
	}
}